// The Flashcards deck store, revault:flashcards:
//   { v: 2, active, opts: { ret, newPerDay, mix, m }, del: { [deckOrCardId]: ts },
//     decks: [{ id, name, m, studiedAt?, cards: [{ id, front, back, m }] }] }
// m is when a deck or card was made or last changed, and del when one was deleted, so study sync
// (shared/merge-save.js) can tell a delete from a card the other device never had. mutate() keeps
// both up to date for every change, wherever it comes from.
// Every write goes through mutate(): read what is stored right now, change it, write it back.
// Nothing keeps a copy around to write later, so cards the Study Helper adds, or a change made
// in another tab, are never erased by a save from a tab that loaded earlier.
import { readJSON, writeJSON, save, subscribe } from "../shared/store.js";
import { repairCardIds, uid } from "./flashcards-import.js";

export const KEY = "revault:flashcards";

export function load() {
	const v = readJSON(KEY, null);
	if (v && typeof v === "object" && Array.isArray(v.decks)) {
		// Same fix on every device, so writing it back is safe even with two tabs open.
		if (repairCardIds(v)) writeJSON(KEY, v);
		return v;
	}
	return { decks: [], active: null };
}

// fn(db) changes the freshly read store in place. Return false from fn to write nothing (the
// change no longer applies, say the deck was deleted elsewhere). Returns true once written,
// false when nothing was written or the browser refused the write (storage full).
export function mutate(fn) {
	const db = load();
	const before = shape(db);
	if (fn(db) === false) return false;
	stamp(before, db, Date.now());
	db.v = 2;
	for (const d of db.decks) if (d && typeof d === "object") delete d._studying; // screen state from older builds
	return save(KEY, db);
}

// Deletes are remembered this long; a device that has been away longer may bring one back.
export const TOMB_MS = 60 * 864e5;
const isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);
// Each deck's name and each card's text, by id.
function shape(db) {
	const decks = new Map(), cards = new Map();
	for (const d of db.decks) {
		if (!isObj(d)) continue;
		decks.set(d.id, d.name);
		for (const c of Array.isArray(d.cards) ? d.cards : []) if (isObj(c)) cards.set(c.id, c.front + "\u0000" + c.back);
	}
	return { decks, cards };
}
// New or changed decks and cards get m = now; ones that are gone get a tombstone in del.
export function stamp(before, db, now) {
	const del = isObj(db.del) ? db.del : {};
	const seenD = new Set(), seenC = new Set();
	for (const d of db.decks) {
		if (!isObj(d)) continue;
		seenD.add(d.id);
		if (!before.decks.has(d.id) ? !Number.isFinite(d.m) : before.decks.get(d.id) !== d.name) d.m = now;
		for (const c of Array.isArray(d.cards) ? d.cards : []) {
			if (!isObj(c)) continue;
			seenC.add(c.id);
			if (!before.cards.has(c.id) ? !Number.isFinite(c.m) : before.cards.get(c.id) !== c.front + "\u0000" + c.back) c.m = now;
		}
	}
	for (const id of before.decks.keys()) if (!seenD.has(id) && id != null) del[id] = now;
	for (const id of before.cards.keys()) if (!seenC.has(id) && id != null) del[id] = now;
	for (const id of Object.keys(del)) if (!Number.isFinite(del[id]) || del[id] < now - TOMB_MS) delete del[id];
	if (Object.keys(del).length) db.del = del; else delete db.del;
}

const raw = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

// Calls fn after the stored value of any of keys really changed. A change notice alone is not
// proof: any page on this origin can post a key name on the store channel, so the value is
// re-read and compared first. Returns the unsubscribe function.
export function onChange(fn, keys = [KEY]) {
	const now = () => keys.map(raw).join("\u0000");
	let last = now();
	const check = () => { const v = now(); if (v === last) return; last = v; fn(); };
	const offs = keys.map((k) => subscribe(k, check));
	return () => offs.forEach((off) => off());
}

// A card's source, from "Make card from highlight" (app/make-card.js): { t: page title, u: address,
// d: "YYYY-MM-DD", c: citation text }. Plain strings, capped; anything else is left off.
const SRC_CAP = { t: 300, u: 2000, d: 10, c: 1000 };
export function cleanSrc(src) {
	if (!isObj(src)) return null;
	const out = {};
	for (const k of Object.keys(SRC_CAP)) if (typeof src[k] === "string" && src[k]) out[k] = src[k].slice(0, SRC_CAP[k]);
	if (out.u && !/^https?:\/\//i.test(out.u)) delete out.u;
	if (out.d && !/^\d{4}-\d{2}-\d{2}$/.test(out.d)) delete out.d;
	return out.t || out.u ? out : null;
}

export function listDecks() { return load().decks.map((d) => ({ id: d.id, name: d.name, count: d.cards.length })); }
// Add cards to a deck (by id, or by name; a new name makes a new deck) in the same store and shape
// as a manual add. Each card may carry a `src` (cleanSrc). Returns { deckId, name, added }, or null
// when the browser couldn't save them.
export function addCards(deckNameOrId, cards) {
	const clean = (Array.isArray(cards) ? cards : []).map((c) => ({ front: String(c.front || "").trim().slice(0, 500), back: String(c.back || "").trim().slice(0, 500), src: cleanSrc(c.src) })).filter((c) => c.front && c.back);
	let out = null;
	const ok = mutate((db) => {
		let deck = db.decks.find((d) => d.id === deckNameOrId) || db.decks.find((d) => d.name === String(deckNameOrId || "").trim());
		if (!deck) { deck = { id: uid(), name: String(deckNameOrId || "").trim().slice(0, 80) || "Study Helper cards", cards: [] }; db.decks.unshift(deck); }
		for (const c of clean) { const card = { id: uid(), front: c.front, back: c.back }; if (c.src) card.src = c.src; deck.cards.push(card); }
		db.active = deck.id;
		out = { deckId: deck.id, name: deck.name, added: clean.length };
	});
	return ok ? out : null;
}
